/**
 * Token Service - Auto-refresh Session Tokens
 *
 * Manages session token lifecycle:
 * - Stores token in localStorage
 * - Auto-refreshes tokens before expiration
 * - Provides token access for API calls
 *
 * Part of Phase 3: Visual Refinement (Authentication Enhancement)
 */

const TOKEN_KEY = 'crux_session_token';
const REFRESH_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000; // 2 days before expiration

interface TokenInfo {
  token: string;
  expiresAt: number;
}

/**
 * Get current session token from localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Store session token in localStorage
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Remove session token from localStorage
 */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Decode token payload (base64) without verification
 * Used to extract expiration time
 */
function decodeToken(token: string): TokenInfo | null {
  try {
    const [payloadBase64] = token.split('.');
    if (!payloadBase64) return null;

    const payloadJson = atob(
      payloadBase64.replace(/-/g, '+').replace(/_/g, '/')
    );
    const payload = JSON.parse(payloadJson);

    return {
      token,
      expiresAt: payload.expiresAt,
    };
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Check if token needs refresh
 * Returns true if token expires within REFRESH_THRESHOLD_MS
 */
export function shouldRefreshToken(): boolean {
  const token = getToken();
  if (!token) return false;

  const tokenInfo = decodeToken(token);
  if (!tokenInfo) return false;

  const now = Date.now();
  const timeUntilExpiration = tokenInfo.expiresAt - now;

  return timeUntilExpiration < REFRESH_THRESHOLD_MS;
}

/**
 * Refresh the current session token
 * Calls the API to get a new token with extended expiration
 */
export async function refreshToken(): Promise<boolean> {
  const currentToken = getToken();
  if (!currentToken) {
    console.warn('No token to refresh');
    return false;
  }

  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    const response = await fetch(`${apiBaseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`,
      },
    });

    if (!response.ok) {
      console.error('Token refresh failed:', response.status);
      return false;
    }

    const data = await response.json();
    if (data.token) {
      setToken(data.token);
      console.log('Token refreshed successfully');
      return true;
    }

    return false;
  } catch (error) {
    console.error('Token refresh error:', error);
    return false;
  }
}

/**
 * Auto-refresh token if needed
 * Call this periodically (e.g., on app mount, route changes, API calls)
 */
export async function autoRefreshToken(): Promise<void> {
  if (shouldRefreshToken()) {
    console.log('Token needs refresh, refreshing...');
    const success = await refreshToken();
    if (!success) {
      console.warn('Auto-refresh failed, user may need to re-authenticate');
    }
  }
}
